import type {
  AdaptiveLogger,
  NewFileTemplateInfo,
} from "@1js/office-online-jsapi-core";
import type { FileType } from "@1js/owl-shared-types";
import type { BehaviorSubject, Observable } from "rxjs";
import type { GraphDriveItem, Wopi } from "./graphDriveItem";
import type {
  AppInitDiagnostics,
  OfficeApplication,
  OfficeDocumentSession,
  RebootOverrideData,
  WacLoadParams,
  WopiPendingApp,
} from "./jsapi";
import type { Template } from "./template";

export const WacParams = [
  "id",
  "openWith",
  "officebundle",
  "currentUserRole",
  "eTag",
  "name",
  "size",
  "content.downloadUrl",
  "file",
  "sharepointIds",
  "sensitivityLabel",
  "webUrl",
  "webDavUrl",
  "parentReference",
  "content.downloadCode",
];

export interface DocumentIdentifier {
  docId?: string;
  driveId?: string;
  docUrl?: string;
  fileName?: string;
  shareId?: string;
  shareUrl?: string;
  baseUrl?: string;
  fileId?: string;
  templateId?: string;
  newFileTemplate?: NewFileTemplateInfo; // Details of the custom template to be used to create the new file.
  docTenantId?: string;
}

export interface SharepointInfo {
  siteUrl: string;
  listItemId?: string;
  listId?: string;
  listItemUniqueId?: string;
}

export interface CreateNewInfo {
  fileType: FileType;
  clickTime: number;
  culture: string;
  fallbackUiCulture?: string;
  fallbackDataCulture?: string;
}

export type DocumentIntent = "edit" | "view" | "open";

export interface DocumentBootInfo extends DocumentIdentifier {
  fileType: FileType; // The document type
  wdOrigin: string; // session origin
  uiCulture: string; // ui language preference, for example: en-us
  dataCulture: string; // data language preference, for example: en-gb
  puid: string; // current user id
  clickTime?: number; // the time when user clicks on the document to open
  navId?: string; // navigation id, which is used for opening a PPT document to a specific slide
  wdValue?: string; // a string of host passed in wd parameters
  isNewFile?: boolean; // if the current document is a newly created document. NOTE: Each app handles newly created, especially zero-byte document, differently.
  isTwoStepCreateNew?: boolean; // should be using the 2-step create new flow to create a new document
  bootstrapperUrl?: string; // download url of JsApi
  rebootOverrideData?: RebootOverrideData; // data used for rebooting a document
  intent?: DocumentIntent; // the intended document open mode
  createNewInfo?: CreateNewInfo;
}

export interface WacPendingLoadData {
  correlationId: string;
  bootstrapperObservable: Observable<WacLoadFunction>;
  fileNamePromise: Promise<string>;
  isNewFile?: boolean;
  applicationUrlPromise: Promise<string | undefined>;
}

export interface WopiHostData {
  bundleStaleness?: number;
  bundleUrl?: string;
  bundleUrlNoAuth?: string;
  bundleCode?: string;
  bundleVersion?: string;
  sharepointSiteUrl?: string;
  downloadUrl: string;
  webUrl?: string;
  eTag: string;
  fileName: string;
  fileSize: number;
  irmEnabled: boolean;
  protectionEnabled: boolean;
  isNewFile: boolean;
  readOnly: boolean;
  blocksDownload?: boolean;
  sharePointInfo?: SharepointInfo;
  wac: Wopi;
  preSeededSessionKey?: string;
  preSeededWacSessionId?: string;
  webDavUrl?: string;
  downloadCode?: string;
  restUrl?: string;
}

export interface WacPerformanceTiming {
  startTime?: number;
  endTime?: number;
}

export interface BootstrapperInfo {
  bootstrapperUrl: string;
  applicationUrl: string;
  performance: WacPerformanceTiming;
}

export interface WopiFetchData {
  documentIdentifier: DocumentIdentifier;
  wopiHostData: WopiHostData;
  fetchDiagnostics: WacPerformanceTiming;
}

export type GraphItemFetchFunction = (
  docBootInfo: DocumentBootInfo,
  forceRefresh: boolean,
  zeroByte: boolean,
  logger?: AdaptiveLogger,
  isDocBoot?: boolean,
  skipEss?: boolean
) => Promise<GraphDriveItem | undefined>;

export interface WacLoadData {
  wopiHostData: WopiHostData;
  officeApplication: OfficeApplication;
  bootedDocumentInfo: BehaviorSubject<DocumentBootInfo>;
  wopiPendingApp?: WopiPendingApp;
  officeDocumentSession?: OfficeDocumentSession;
  wacBootDiagnostics?: AppInitDiagnostics;
}

export type CreateDocumentFunction = (
  createNewInfo: CreateNewInfo,
  template?: Template
) => Promise<string>;

export type WacLoadFunction = (data: WacLoadParams) => Observable<WacLoadData>;
